import { Button } from '@ai-tutor/ui';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Loader2,
  RotateCw,
} from 'lucide-react';

import type { LessonDepth } from '@/lib/api-client';
import { formatUsd, type PurchaseSummary } from '@/lib/billing';
import { parseUtcDate } from '@/lib/datetime';
import {
  DEPTH_OPTIONS,
  FLOW_PRESETS,
  type FlowPreset,
} from '@/config/lesson-settings-options';

import { StepLayout } from '../StepLayout';

interface CompleteStepProps {
  depth: LessonDepth;
  flow: FlowPreset;
  /** Leaves onboarding for the lesson composer at `/`. */
  onEnter: () => void;
  /** The `onboarded` write is still in flight. */
  loading?: boolean;
  /** The `onboarded` write failed — see the note on the retry branch. */
  failed?: boolean;
  onRetry: () => void;
  /**
   * The plan bought on the pricing step, when the user paid. Absent when they
   * stayed on Free (or started the trial), and the screen reads as before.
   */
  purchase?: PurchaseSummary;
}

/**
 * Final screen. `onboarded` was already flipped when the pricing step finished,
 * so this normally just confirms and hands off — but it is also the only place
 * that write's failure can surface, which is why it has two states.
 */
export function CompleteStep({
  depth,
  flow,
  onEnter,
  loading = false,
  failed = false,
  onRetry,
  purchase,
}: CompleteStepProps) {
  if (failed) return <CompleteStepFailed loading={loading} onRetry={onRetry} />;

  return (
    <StepLayout
      variant='terminal'
      title='Your space is ready'
      description={`${summarise(depth, flow)} You can change any of it in Settings.`}
      actions={
        <Button size='lg' onClick={onEnter} disabled={loading}>
          {loading ? (
            <>
              <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
              Finishing…
            </>
          ) : (
            <>
              Create my first lesson
              <ArrowRight className='ml-1.5 h-4 w-4' />
            </>
          )}
        </Button>
      }
    >
      {purchase && <PurchaseConfirmation purchase={purchase} />}
    </StepLayout>
  );
}

/**
 * The receipt line for a user who paid on the pricing step. Without it the
 * last thing they saw about their money was Stripe's card form: this screen
 * spoke only of lesson depth, and nothing said the payment had gone through,
 * what it cost, or what the renewal will.
 */
function PurchaseConfirmation({ purchase }: { purchase: PurchaseSummary }) {
  const renews = purchase.renewsAt
    ? `, renews ${formatRenewal(purchase.renewsAt)} at ${formatUsd(
        purchase.renewalCents
      )}`
    : '';
  return (
    <p
      data-testid='onboarding-plan-confirmation'
      className='mx-auto flex max-w-md items-start justify-center gap-2 rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-left text-body text-foreground'
    >
      <CheckCircle2
        aria-hidden='true'
        className='mt-0.5 h-4 w-4 shrink-0 text-success'
      />
      <span>
        You’re on <strong>{purchase.planName}</strong> —{' '}
        {formatUsd(purchase.chargedCents)} today{renews}.
      </span>
    </p>
  );
}

function formatRenewal(iso: string): string {
  return parseUtcDate(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * The three answers, as one sentence.
 *
 * They used to be read back in a divided three-row table with an uppercase
 * micro-label on each row - a lot of chrome for nine words, and three eyebrows
 * inside one small component. A sentence says the same thing and leaves the
 * only accent on the screen where the user is meant to look, which is the
 * button.
 */
function summarise(depth: LessonDepth, flow: FlowPreset) {
  const depthLabel = DEPTH_OPTIONS.find(o => o.value === depth)?.label;
  const flowLabel = FLOW_PRESETS.find(o => o.value === flow)?.label;
  if (!depthLabel || !flowLabel) return 'Your learning space is set up.';
  return `Every lesson will be ${depthLabel} depth, in the ${flowLabel} format.`;
}

/**
 * The `onboarded` write failed.
 *
 * There is deliberately no way forward from here. `OnboardingGate` redirects
 * anyone whose profile still says `onboarded: false` straight back to
 * `/onboarding`, so offering "continue anyway" would bounce the user to step 1
 * and drop every answer — an unbreakable loop with no explanation, which is
 * exactly what this screen used to do while showing "You're all set!".
 */
function CompleteStepFailed({
  loading,
  onRetry,
}: {
  loading: boolean;
  onRetry: () => void;
}) {
  return (
    <StepLayout
      variant='terminal'
      icon={
        <AlertCircle
          aria-hidden='true'
          className='mx-auto mb-3 h-8 w-8 text-destructive'
        />
      }
      title='We couldn’t finish setting up'
      description='Your answers are safe — we just couldn’t save them. Check your connection and try again.'
      actions={
        <Button size='lg' onClick={onRetry} disabled={loading}>
          {loading ? (
            <>
              <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
              Retrying…
            </>
          ) : (
            <>
              <RotateCw className='mr-1.5 h-4 w-4' />
              Try again
            </>
          )}
        </Button>
      }
    />
  );
}
